import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, FileText, Upload } from "lucide-react";

type ResumeParseStatus = "idle" | "parsing" | "success" | "error";

interface ResumeUploadSectionProps {
  resumeFile: File | null;
  parseStatus: ResumeParseStatus;
  isVerifyingCaptcha: boolean;
  recaptchaLoaded: boolean;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRetry: () => void;
}

export function ResumeUploadSection({
  resumeFile,
  parseStatus,
  isVerifyingCaptcha,
  recaptchaLoaded,
  onFileChange,
  onRetry,
}: ResumeUploadSectionProps) {
  const getStatusIcon = () => {
    switch (parseStatus) {
      case "parsing":
        return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case "error":
        return <FileText className="h-4 w-4 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (parseStatus) {
      case "parsing":
        return isVerifyingCaptcha
          ? "Verifying security..."
          : "Parsing resume...";
      case "success":
        return "Resume parsed successfully!";
      case "error":
        return "Failed to parse resume";
      default:
        return resumeFile ? resumeFile.name : "";
    }
  };

  const isParsing = parseStatus === "parsing";
  const isDisabled = isParsing || isVerifyingCaptcha || !recaptchaLoaded;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== "application/pdf") {
        alert("Please select only PDF files");
        event.target.value = ""; // Clear the input
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        event.target.value = ""; // Clear the input
        return;
      }
    }
    onFileChange(event);
  };

  return (
    <div className="mb-6">
      <div>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="flex-1"
              disabled={isDisabled}
            />
            {!recaptchaLoaded && (
              <div className="text-xs text-yellow-600">Loading security...</div>
            )}
          </div>
          {resumeFile && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
              {getStatusIcon()}
              <div className="flex-1">
                <div className="font-medium text-sm">{getStatusText()}</div>
                {parseStatus === "parsing" && (
                  <div className="text-xs text-gray-600 mt-1">
                    {isVerifyingCaptcha
                      ? "Completing security check..."
                      : "This may take a few seconds..."}
                  </div>
                )}
                {parseStatus === "success" && (
                  <div className="text-xs text-green-600 mt-1">
                    Form fields auto-filled. Please review below.
                  </div>
                )}
                {parseStatus === "error" && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="text-sm text-red-600">
                      Resume upload complete. Please retry if details are
                      incorrect.
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRetry}
                      disabled={isDisabled}
                    >
                      {isVerifyingCaptcha ? "Verifying..." : "Retry"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
